// AsyncLocalStorage for server-side correlation context
type CorrelationStorage = {
  getStore(): string | undefined;
  run<T>(store: string, callback: () => T | Promise<T>): T | Promise<T>;
};

let correlationStorage: CorrelationStorage | null = null;
// Only try to load async_hooks on server-side
if (typeof window === "undefined" && typeof process !== "undefined") {
  try {
    const { AsyncLocalStorage } = require("async_hooks");
    correlationStorage = new AsyncLocalStorage();
  } catch {
    // Not available in this environment
  }
}

/**
 * Generate a simple correlation ID
 */
export function generateCorrelationId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `${timestamp}-${random}`;
}

/**
 * Get current correlation ID from AsyncLocalStorage
 */
export function getCorrelationId(): string | null {
  return correlationStorage?.getStore() || null;
}

/**
 * Run a function with a specific correlation ID context
 */
export function runWithCorrelationId<T>(
  correlationId: string,
  fn: () => T | Promise<T>,
): T | Promise<T> {
  if (correlationStorage) {
    return correlationStorage.run(correlationId, fn);
  }
  return fn();
}

/**
 * Extract correlation ID from request headers
 */
export function extractCorrelationId(headers: Headers): string | null {
  return headers.get("X-Correlation-ID");
}

/**
 * Read an incoming correlation ID or generate one when absent.
 */
export function resolveCorrelationId(headers: Headers): string {
  return extractCorrelationId(headers) || generateCorrelationId();
}

// Client-side correlation ID management
const CORRELATION_ID_KEY = "cymonz-correlation-id";

// Fallback in-memory storage when sessionStorage is unavailable
let memoryCorrelationId: string | null = null;

/**
 * Get or create correlation ID for client-side use
 * Uses sessionStorage for persistence across page navigation
 */
export function getClientCorrelationId(): string | null {
  if (typeof window === "undefined") return null;

  try {
    const stored = window.sessionStorage.getItem(CORRELATION_ID_KEY);
    return stored || memoryCorrelationId;
  } catch (error) {
    // SessionStorage might not be available (private mode, etc.)
    return memoryCorrelationId;
  }
}

/**
 * Set correlation ID for client-side use
 * Stores in sessionStorage for persistence
 */
export function setClientCorrelationId(correlationId: string): void {
  if (typeof window === "undefined") return;

  // Always set in-memory fallback
  memoryCorrelationId = correlationId;

  try {
    window.sessionStorage.setItem(CORRELATION_ID_KEY, correlationId);
  } catch (error) {
    // SessionStorage might not be available (private mode, etc.)
    console.warn(
      "Unable to store correlation ID in sessionStorage, using memory fallback:",
      error,
    );
  }
}

/**
 * Get or generate a correlation ID for client-side use
 * If no correlation ID exists, generates a new one and stores it
 */
export function ensureClientCorrelationId(): string {
  let correlationId = getClientCorrelationId();

  if (!correlationId) {
    correlationId = generateCorrelationId();
    setClientCorrelationId(correlationId);
  }

  return correlationId;
}

/**
 * Clear the client-side correlation ID
 * Useful for starting new user sessions
 */
export function clearClientCorrelationId(): void {
  if (typeof window === "undefined") return;

  // Clear memory fallback
  memoryCorrelationId = null;

  try {
    window.sessionStorage.removeItem(CORRELATION_ID_KEY);
  } catch (error) {
    // SessionStorage might not be available
  }
}
